// Phase 2 — "By Merchant (Line Items)" tab. Contains the merchant-level
// breakdown table with expandable per-effective-date detail rows + the
// CSV export link. KPI strip shows partner payout + total volume for the
// current filter set.

import React from "react";
import { Section, Badge, Btn, Select } from "@/components/Shared";
import { Download, ChevronRight, ChevronDown } from "lucide-react";
import { fmtINR, fmtCompact, fmtPeriod } from "@/lib/api";

export default function PayoutsByMerchantTab({
  merchantRows, mTotals, period,
  partnerOptions, gatewayOptions, lobOptions,
  partnerFilter, setPartnerFilter,
  gatewayFilter, setGatewayFilter,
  lobFilter, setLobFilter,
  expandedRows, toggleExpand,
  formatShare, exportEffectiveDateBreakdown,
}) {
  return (
    <div className="p-4 sm:p-10 space-y-4" data-testid="merchant-view">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-56">
          <Select
            testid="merchant-partner-filter"
            placeholder="All partners"
            options={partnerOptions}
            value={partnerFilter}
            onChange={setPartnerFilter}
          />
        </div>
        <div className="w-44">
          <Select
            testid="merchant-gateway-filter"
            placeholder="All gateways"
            options={gatewayOptions}
            value={gatewayFilter}
            onChange={setGatewayFilter}
          />
        </div>
        <div className="w-48">
          <Select
            testid="merchant-lob-filter"
            placeholder="All LOBs"
            options={lobOptions}
            value={lobFilter}
            onChange={setLobFilter}
          />
        </div>
        {(partnerFilter || gatewayFilter || lobFilter) && (
          <Btn variant="ghost" testid="merchant-clear-filters" onClick={() => { setPartnerFilter(""); setGatewayFilter(""); setLobFilter(""); }}>Clear filters</Btn>
        )}
        <div className="text-[12px] text-[var(--muted)] ml-auto">{merchantRows.length} line item(s){period ? ` · ${fmtPeriod(period)}` : " · all periods"}</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-[var(--surface)] border border-[var(--line)] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Partner's Payout</div><div className="num text-xl font-semibold mt-1.5 text-[var(--accent)]">₹ {fmtCompact(mTotals.payout)}</div></div>
        <div className="bg-[var(--surface)] border border-[var(--line)] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Total Volume</div><div className="num text-xl font-semibold mt-1.5">₹ {fmtCompact(mTotals.vol)}</div></div>
      </div>

      <Section title="Merchant-level breakdown" testid="merchant-breakdown">
        <div className="overflow-auto max-h-[640px]">
          <div className="overflow-x-auto"><table className="table-data w-full">
            <thead><tr>
              <th className="w-8"></th>
              <th>Period</th><th>Merchant ID</th><th>Merchant</th><th>Gateway</th><th>LOB</th>
              <th className="text-right">Volume</th>
              <th>Primary Partner</th>
              <th className="text-right">Share</th>
              <th className="text-right">Payout</th>
              <th>Secondary Partner</th>
              <th className="text-right">Share</th>
              <th className="text-right">Payout</th>
              <th className="text-right">Total Payout</th>
              <th>Status</th>
            </tr></thead>
            <tbody>
              {merchantRows.map((r) => {
                const hasSecondary = !!r.secondary_partner_id && r.secondary_payout > 0;
                const total = (r.primary_payout || 0) + (r.secondary_payout || 0);
                const expandKey = `${r.period}|${r.merchant_id}|${r.gateway}|${r.lob}`;
                const isExpanded = expandedRows.has(expandKey);
                const subLines = r.effective_date_lines || [];
                const isExpandable = subLines.length > 1 || (subLines.length === 1 && !!subLines[0].effective_date);
                return (
                  <React.Fragment key={expandKey}>
                    <tr data-testid={`merchant-row-${r.merchant_id}-${r.period}`}>
                      <td className="text-center">
                        {isExpandable ? (
                          <button
                            data-testid={`merchant-expand-${r.merchant_id}-${r.period}`}
                            onClick={() => toggleExpand(expandKey)}
                            className="inline-flex items-center justify-center w-5 h-5 rounded-sm hover:bg-[var(--surface-2)] text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
                            aria-label={isExpanded ? "Collapse details" : "Expand effective-date details"}
                          >
                            {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          </button>
                        ) : null}
                      </td>
                      <td className="num font-medium">{fmtPeriod(r.period)}</td>
                      <td className="num">{r.merchant_id}</td>
                      <td className="font-medium">{r.merchant_name}</td>
                      <td>{r.gateway}</td>
                      <td className="text-[12px]">{r.lob}</td>
                      <td className="text-right num">₹ {fmtINR(r.processed_volume, 0)}</td>
                      <td className="text-[12px]">{r.primary_partner_name || <span className="text-[var(--muted)]">—</span>}</td>
                      <td className="text-right num text-[12px]">{formatShare(r.primary_partner_id, r.primary_share_pct, r.primary_share_value)}</td>
                      <td className="text-right num text-[var(--accent)]">{fmtINR(r.primary_payout, 0)}</td>
                      <td className="text-[12px]">
                        {hasSecondary ? (
                          <>
                            {r.secondary_partner_name}
                            {(r.co_partners || []).length > 1 && (
                              <span
                                data-testid={`co-partner-more-${r.merchant_id}-${r.period}`}
                                title={(r.co_partners || []).map((c) => `${c.partner_name} · ${(c.share_pct * 100).toFixed(2)}%`).join("\n")}
                                className="ml-1.5 text-[10px] uppercase tracking-wider px-1.5 py-[1px] rounded-sm bg-[var(--surface-3)] text-[var(--muted)] border border-[var(--line-2)] cursor-help"
                              >
                                +{(r.co_partners || []).length - 1} more
                              </span>
                            )}
                          </>
                        ) : <span className="text-[var(--muted)]">—</span>}
                      </td>
                      <td className="text-right num text-[12px]">{hasSecondary ? formatShare(r.secondary_partner_id, r.secondary_share_pct, r.secondary_share_value) : <span className="text-[var(--muted)]">—</span>}</td>
                      <td className="text-right num text-[var(--accent)]">{hasSecondary ? fmtINR(r.secondary_payout, 0) : <span className="text-[var(--muted)]">—</span>}</td>
                      <td className="text-right num font-semibold text-[var(--accent)]">{fmtINR(total, 0)}</td>
                      <td>{r.processed_volume > 0 ? <Badge tone="positive">OK</Badge> : <Badge tone="muted">No Vol</Badge>}</td>
                    </tr>
                    {isExpanded && subLines.length > 0 && (
                      <tr className="bg-[var(--surface-2)]" data-testid={`merchant-detail-${r.merchant_id}-${r.period}`}>
                        <td colSpan={15} className="p-0">
                          <div className="px-8 py-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">
                                Effective-date breakdown · {subLines.length} entr{subLines.length === 1 ? "y" : "ies"}
                              </div>
                              <button
                                data-testid={`merchant-detail-export-${r.merchant_id}-${r.period}`}
                                onClick={() => exportEffectiveDateBreakdown(r)}
                                className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-1"
                                title="Download this merchant's per-effective-date breakdown as CSV"
                              >
                                <Download size={12} /> Export CSV
                              </button>
                            </div>
                            <table className="table-data w-full text-[12px]">
                              <thead>
                                <tr>
                                  <th>Effective Date</th>
                                  <th className="text-right">Volume</th>
                                  <th>Primary Partner</th>
                                  <th className="text-right">Share</th>
                                  <th className="text-right">Payout</th>
                                  <th>Secondary Partner</th>
                                  <th className="text-right">Share</th>
                                  <th className="text-right">Payout</th>
                                  <th className="text-right">Line Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {subLines.map((s) => {
                                  const lineTotal = (s.primary_payout || 0) + (s.secondary_payout || 0);
                                  const subHasSec = !!s.secondary_partner_id && s.secondary_payout > 0;
                                  const dateLabel = s.effective_date || s.rate_anchor_date || "—";
                                  const dateTitle = s.effective_date
                                    ? `Volume effective from ${s.effective_date}`
                                    : (s.rate_anchor_date ? `Anchored to period end: ${s.rate_anchor_date}` : "No effective date");
                                  return (
                                    <tr key={s.effective_date || s.rate_anchor_date || "legacy"} data-testid={`merchant-detail-line-${r.merchant_id}-${r.period}-${dateLabel}`}>
                                      <td className="num" title={dateTitle}>{dateLabel}</td>
                                      <td className="text-right num">₹ {fmtINR(s.processed_volume, 0)}</td>
                                      <td>{s.primary_partner_name || <span className="text-[var(--muted)]">—</span>}</td>
                                      <td className="text-right num">{formatShare(s.primary_partner_id, s.primary_share_pct, s.primary_share_value)}</td>
                                      <td className="text-right num text-[var(--accent)]">{fmtINR(s.primary_payout, 0)}</td>
                                      <td>{subHasSec ? s.secondary_partner_name : <span className="text-[var(--muted)]">—</span>}</td>
                                      <td className="text-right num">{subHasSec ? formatShare(s.secondary_partner_id, s.secondary_share_pct, s.secondary_share_value) : <span className="text-[var(--muted)]">—</span>}</td>
                                      <td className="text-right num text-[var(--accent)]">{subHasSec ? fmtINR(s.secondary_payout, 0) : <span className="text-[var(--muted)]">—</span>}</td>
                                      <td className="text-right num font-semibold text-[var(--accent)]">{fmtINR(lineTotal, 0)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!merchantRows.length && (
                <tr><td colSpan={15} className="text-center text-[var(--muted)] py-8">No line items for the selected filters.</td></tr>
              )}
            </tbody>
          </table></div>
        </div>
      </Section>
    </div>
  );
}
